/**
 * Shared helpers for Sales / Loyalty / Airline promotion searchByCriteria payloads.
 */

export function parseToArray(val) {
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'string') {
        const trimmed = val.trim();
        if (!trimmed) return [];
        return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
}

export function toActionValue(type, val) {
    if (type === 'all') return { action: 'all', value: [] };
    const action = type === 'include' || type === 'exclude' ? type : '';
    const value = action ? parseToArray(val) : [];
    return { action, value };
}

export function toFeePayload(val) {
    const str = typeof val === 'string' ? val.trim() : '';
    const num = str ? parseFloat(str) : undefined;
    const checked = str !== '' || (num !== undefined && !isNaN(num));
    return {
        checked: !!checked,
        ...(num !== undefined && !isNaN(num) ? { value: num } : {}),
    };
}

function toDateRange(restriction, from, to) {
    return {
        type: restriction === 'specific_duration' ? 'specific' : 'no_restriction',
        from: from || undefined,
        to: to || undefined,
    };
}

/**
 * Maps search form values to searchByCriteria API payload.
 * @param {object} form - react-hook-form values
 * @param {{ isLoyaltyPromotions?: boolean, isAirlinePromotions?: boolean, promotionType?: 'loyalty'|'sales'|'airline' }} [options]
 */
export function mapFormToSearchPayload(form, options = {}) {
    const promotionType = options.promotionType
        || (options.isAirlinePromotions ? 'airline' : options.isLoyaltyPromotions ? 'loyalty' : 'sales');
    const isLoyaltyPromotions = promotionType === 'loyalty';
    const isAirlinePromotions = promotionType === 'airline';

    const sharedCriteria = {
        dateRange: toDateRange(form.restriction, form.reservationDateFrom, form.reservationDateTo),
        departureDateRange: toDateRange(
            form.departureRestriction,
            form.departureDateFrom,
            form.departureDateTo,
        ),
        origin: toActionValue(form.originType, form.origin?.toUpperCase?.() || form.origin),
        destination: toActionValue(form.destinationType, form.destination?.toUpperCase?.() || form.destination),
        brandCode: toActionValue(form.brandCodeType, form.brandCode?.toUpperCase?.() || form.brandCode),
        airline: toActionValue(form.airlineType, form.airline?.toUpperCase?.() || form.airline),
        bookingChannel: Array.isArray(form.bookingChannel) ? form.bookingChannel : [],
        supplier: Array.isArray(form.supplier) ? form.supplier : [],
        tripType: Array.isArray(form.tripTypes) ? form.tripTypes : [],
        cabinType: Array.isArray(form.cabinTypes) ? form.cabinTypes : [],
    };

    if (isAirlinePromotions) {
        const originVal = form.origin?.toUpperCase?.() || form.origin;
        const destinationVal = form.destination?.toUpperCase?.() || form.destination;
        const airlineVal = form.airline?.toUpperCase?.() || form.airline;
        return {
            dateRange: sharedCriteria.dateRange,
            departureDateRange: sharedCriteria.departureDateRange,
            origin: toActionValue(originVal ? 'include' : '', originVal),
            destination: toActionValue(destinationVal ? 'include' : '', destinationVal),
            airline: toActionValue(airlineVal ? 'include' : '', airlineVal),
            supplier: sharedCriteria.supplier,
            tripType: sharedCriteria.tripType,
            cabinType: sharedCriteria.cabinType,
            promotionCode: form.promotionCode?.trim?.() || '',
        };
    }

    const payload = {
        ...sharedCriteria,
        consortium: toActionValue(form.consortiumType, form.consortium ? String(form.consortium) : ''),
        agencyLocation: toActionValue(form.agencyLocationType, form.agencyLocation?.toUpperCase?.() || form.agencyLocation),
        agency: toActionValue(form.agencyType, form.agency?.value ? [form.agency.value] : ''),
        agent: toActionValue(form.agentType, form.agent?.value ? [form.agent.value] : ''),
    };

    if (isLoyaltyPromotions) {
        payload.isLoyaltyPromotions = true;
        payload.promotionCode = form.promotionCode?.trim?.() || '';
        payload.loyaltyPoints = form.loyaltyPoints?.trim?.() || '';
        if (form.overrideNoProfitNoPoints === true) {
            payload.overrideNoProfitNoPoints = true;
        }
        return payload;
    }

    payload.voidAllowed = !!form.voidAllowed;
    payload.refundAllowed = !!form.refundAllowed;
    payload.reissueAllowed = !!form.reissueAllowed;
    payload.ticketingFee = toFeePayload(form.ticketingFee);
    payload.voidFee = toFeePayload(form.voidFee);
    payload.refundFee = toFeePayload(form.refundFee);
    payload.reissueFee = toFeePayload(form.reissueFee);
    payload.bookingFee = toFeePayload(form.bookingFee);
    payload.merchantFee = toFeePayload(form.merchantFee);
    payload.pubFareCommission = toFeePayload(form.pubCommissionAmount);
    payload.lowestOfOrMarkup = toFeePayload(form.netLowestOfThisOrMarkup);
    payload.defaultMarkup = toFeePayload(form.netDefaultMarkup);
    payload.sellPubAtCost = !!form.sellPubAtCost;
    return payload;
}

function isActionValueEmpty(obj) {
    return !obj || (obj.action === '' && (!obj.value || obj.value.length === 0));
}

function isFeePayloadEmpty(obj) {
    return !obj || (obj.checked === false && (obj.value === undefined || obj.value === null));
}

export function stripEmptyPayloadKeys(payload) {
    const result = {};
    for (const key of Object.keys(payload)) {
        const val = payload[key];
        if (val === undefined || val === null) continue;
        if (val === '') continue;
        if (Array.isArray(val) && val.length === 0) continue;
        if (val === false) continue;
        if (key === 'dateRange' || key === 'departureDateRange') {
            const dr = val;
            if (!dr || dr.type === 'no_restriction') continue;
            result[key] = { type: dr.type, ...(dr.from && { from: dr.from }), ...(dr.to && { to: dr.to }) };
            continue;
        }
        if (typeof val === 'object' && val !== null && !Array.isArray(val) && 'action' in val && 'value' in val) {
            if (isActionValueEmpty(val)) continue;
            result[key] = val;
            continue;
        }
        if (typeof val === 'object' && val !== null && !Array.isArray(val) && 'checked' in val) {
            if (isFeePayloadEmpty(val)) continue;
            result[key] = val;
            continue;
        }
        result[key] = val;
    }
    return result;
}

export function hasSearchCriteria(payload) {
    if (payload.dateRange?.type === 'specific') return true;
    if (payload.departureDateRange?.type === 'specific') return true;
    const hasActionValue = (obj) => obj && (obj.action === 'include' || obj.action === 'exclude');
    if (hasActionValue(payload.consortium)) return true;
    if (hasActionValue(payload.agencyLocation)) return true;
    if (hasActionValue(payload.agency)) return true;
    if (hasActionValue(payload.agent)) return true;
    if (hasActionValue(payload.origin)) return true;
    if (hasActionValue(payload.destination)) return true;
    if (hasActionValue(payload.brandCode)) return true;
    if (hasActionValue(payload.airline)) return true;
    if (payload.bookingChannel?.length) return true;
    if (payload.supplier?.length) return true;
    if (payload.tripType?.length) return true;
    if (payload.cabinType?.length) return true;
    if (payload.voidAllowed || payload.refundAllowed || payload.reissueAllowed) return true;
    if (payload.ticketingFee?.checked || payload.voidFee?.checked || payload.refundFee?.checked ||
        payload.reissueFee?.checked || payload.bookingFee?.checked || payload.merchantFee?.checked) return true;
    if (payload.pubFareCommission?.checked || payload.lowestOfOrMarkup?.checked || payload.defaultMarkup?.checked) return true;
    if (payload.sellPubAtCost) return true;
    if (payload.promotionCode) return true;
    if (payload.loyaltyPoints) return true;
    if (payload.overrideNoProfitNoPoints) return true;
    return false;
}
